/// <amd-module name="Repos/PullRequest/Attachments/PullRequestAttachmentCommand" />
import * as React from "react";
import { IPullRequestMarkdownToolbarContext } from "Repos/Common/Contracts/Markdown";
import { getFileExtension } from "Repos/Common/Util/File";
import * as Resources from "Repos/PullRequest/Attachments/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { equals, format } from "VSS/Core/Util/String";
import { IMarkdownToolbarCommand } from "VSS/Features/MarkdownEditor/Commands/Toolbar";
import { insertText } from "VSS/Features/MarkdownEditor/Formatting/Common";
import { IMarkdownFormattingArgs, IMarkdownFormattingResult } from "VSS/Features/MarkdownEditor/Formatting/Contracts";
import { MessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { FileInput, FileInputContentType, FileInputData } from "VSSUI/FileInput";
import { IIconProps } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";

const MaxAttachmentSizeMB = 25;
const MaxAttachmentSize = MaxAttachmentSizeMB * 1024 * 1024;

const AllowedAttachmentExtensions = [
    "PNG",
    "GIF",
    "JPG",
    "JPEG",
    "DOCX",
    "PPTX",
    "XLSX",
    "TXT",
    "PDF",
    "ZIP",
    "GZ",
    "LYR",
    "MOV",
    "MP4",
    "ICO",
    "CSV"
];

export interface IPullRequestAttachmentCommandProps {
    addAttachmentToMarkdown: (
        pullRequestContext: IPullRequestMarkdownToolbarContext,
        files: File[],
        formattingArgs: IMarkdownFormattingArgs,
        errorHandler: (error: Error) => void
    ) => Promise<IMarkdownFormattingResult>;
    pageContext: IVssPageContext;
    pullRequestContext: IPullRequestMarkdownToolbarContext;
}

export class PullRequestAttachmentCommand implements IMarkdownToolbarCommand {
    private props: IPullRequestAttachmentCommandProps;

    public id = "pr-attachments";
    public iconProps: IIconProps = { iconName: "Attach" };
    public text = Resources.ToolbarCommandText;
    public rank = 120;

    constructor(props: IPullRequestAttachmentCommandProps) {
        this.props = props;
    }

    public formatText = (
        formattingArgs: IMarkdownFormattingArgs,
        errorHandler: (error: Error) => void
    ): Promise<IMarkdownFormattingResult | void> => {
        const { pageContext, pullRequestContext, addAttachmentToMarkdown } = this.props;

        const attachmentsValid = new ObservableValue(false);
        let files: FileInputData[] = [];

        return new Promise<IMarkdownFormattingResult | void>((resolve, reject) => {
            const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout(onDismiss => (
                <Observer attachmentsValid={attachmentsValid}>
                    {(props: { attachmentsValid: boolean }) => (
                        <MessageDialog
                            lightDismiss={false}
                            title={Resources.UploadAttachmentTitle}
                            okButtonProps={{
                                disabled: !props.attachmentsValid,
                                text: Resources.UploadButtonText
                            }}
                            onClose={async result => {
                                if (result) {
                                    try {
                                        resolve(
                                            await addAttachmentToMarkdown(
                                                pullRequestContext,
                                                files.map(f => f.result.file!),
                                                formattingArgs,
                                                errorHandler
                                            )
                                        );
                                    } catch (e) {
                                        reject(e);
                                    }
                                } else {
                                    resolve(insertText(formattingArgs));
                                }

                                onDismiss();
                            }}
                            message={
                                <FileInput
                                    allowedFileExtensions={AllowedAttachmentExtensions}
                                    fileNamesCaseSensitive={false}
                                    maximumNumberOfFiles={1}
                                    maximumSingleFileSize={MaxAttachmentSize}
                                    resultContentType={FileInputContentType.RawFile}
                                    updateHandler={updateEvent => {
                                        attachmentsValid.value = updateEvent.files.length > 0;
                                        files = updateEvent.files;
                                    }}
                                />
                            }
                        />
                    )}
                </Observer>
            ));
        });
    };
}

export function validateFile(file: File): void {
    // Check file extension
    const fileExt = getFileExtension(file.name);
    if (!AllowedAttachmentExtensions.some(ext => equals(ext, fileExt, true))) {
        throw new Error(format(Resources.AttachmentTypeValidationError, fileExt));
    }

    // Check file size
    if (file.size > MaxAttachmentSize) {
        throw new Error(format(Resources.AttachmentSizeValidationError, MaxAttachmentSizeMB));
    }
}
